import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'apps/piqet-angular/src/environments/environment';
import { ClipboardHelper } from '@piqet-angular/shared';

interface Endpoint {
  id: string;
  name: string;
  method: string;
  url: string;
  description: string;
  isExpanded: boolean;
  requestBody: PropertyDef[];
  responseBody: PropertyDef[];
  responseStatus: string;
  notes?: AuthNote[];
}

interface PropertyDef {
  name: string;
  type: string;
  required: boolean;
  description: string;
  nullable?: boolean;
  constraints?: string;
}

interface AuthNote {
  title: string;
  content: string;
  type: 'warning' | 'info';
}

interface FlowStep {
  number: number;
  title: string;
  meta: string;
}

interface CodeExample {
  lang: string;
  label: string;
  code: string;
}

interface ResponseExample {
  status: string;
  statusClass: string;
  label: string;
  code: string;
}

interface NavSection {
  title: string;
  children?: NavChild[];
  items?: NavItem[];
}

interface NavItem {
  label: string;
  route?: string;
  isExpanded: boolean;
  children: NavChild[];
}

interface NavChild {
  label: string;
  method?: string;
  methodClass?: string;
  isActive: boolean;
  route?: string;
  sectionKey?: string;
  queryParams?: { [key: string]: string };
}

@Component({
  selector: 'piqet-angular-auth-api-doc',
  templateUrl: './auth-api-doc.component.html',
  styleUrls: ['./auth-api-doc.component.scss'],
})
export class AuthApiDocComponent {
  private piqetLcaApiUrl = environment.piqetLcaApiUrl;

  activeCodeLang = 'curl';
  activeRespStatus = '200';
  isTryPanelOpen = false;
  searchQuery = '';
  reqCopied = false;
  respCopied = false;

  apiBaseUrl = this.piqetLcaApiUrl + '/auth/login';

  navSections: NavSection[] = [
    {
      title: 'Get started',
      children: [
        { label: 'Introduction', isActive: false, sectionKey: 'overview' },
        { label: 'Quickstart', isActive: false, sectionKey: 'try-it' },
        { label: 'Base URLs & environments', isActive: false, sectionKey: 'endpoint-url' },
      ],
    },
    {
      title: 'Authentication',
      items: [
        {
          label: 'JWT authentication',
          isExpanded: true,
          children: [
            { label: 'Overview', isActive: true, sectionKey: 'overview' },
            {
              label: 'Login',
              method: 'POST',
              methodClass: 'method-post',
              isActive: false,
              sectionKey: 'login',
            },
            {
              label: 'Refresh',
              method: 'POST',
              methodClass: 'method-post',
              isActive: false,
              sectionKey: 'refresh',
            },
            { label: 'Token structure', isActive: false, sectionKey: 'token-structure' },
          ],
        },
      ],
    },
    {
      title: 'LCA calculations',
      items: [
        {
          label: 'Impact calculator',
          route: '/docs/lca-api',
          isExpanded: false,
          children: [
            {
              label: 'Calculate impact',
              method: 'POST',
              methodClass: 'method-post',
              isActive: false,
              route: '/docs/lca-api',
            },
            {
              label: 'Retrieve calculation',
              method: 'GET',
              methodClass: 'method-get',
              isActive: false,
              route: '/docs/lca-api',
            },
            { label: 'Main request object', isActive: false, route: '/docs/lca-api' },
            { label: 'Scenario object', isActive: false, route: '/docs/lca-api' },
            { label: 'Packing levels', isActive: false, route: '/docs/lca-api' },
            { label: 'Product details', isActive: false, route: '/docs/lca-api' },
            { label: 'Component details', isActive: false, route: '/docs/lca-api' },
            { label: 'Transportation', isActive: false, route: '/docs/lca-api' },
            { label: 'Distribution', isActive: false, route: '/docs/lca-api' },
          ],
        },
      ],
    },
    {
      title: 'Quick reference',
      children: [
        {
          label: 'Impact indicators',
          isActive: false,
          route: '/docs/quick-reference',
          queryParams: { section: 'indicators' },
        },
        {
          label: 'Material catalog',
          isActive: false,
          route: '/docs/quick-reference',
          queryParams: { section: 'materials' },
        },
        {
          label: 'Transport options',
          isActive: false,
          route: '/docs/quick-reference',
          queryParams: { section: 'transport-modes' },
        },
        {
          label: 'Storage options',
          isActive: false,
          route: '/docs/quick-reference',
          queryParams: { section: 'storage-types' },
        },
      ],
    },
  ];

  authFlowSteps: FlowStep[] = [
    { number: 1, title: 'Submit credentials', meta: 'POST /auth/login' },
    { number: 2, title: 'Receive tokens', meta: 'access + refresh' },
    { number: 3, title: 'Authorize requests', meta: 'Bearer {access}' },
    { number: 4, title: 'Refresh on expiry', meta: 'POST /auth/refresh' },
  ];

  endpoints: Endpoint[] = [
    {
      id: 'login',
      name: 'Authenticate user',
      method: 'POST',
      url: '/api/v1/auth/login',
      description:
        'Exchanges user credentials for an access token and a refresh token. Failed attempts are rate-limited per IP and per username.',
      isExpanded: true,
      requestBody: [
        {
          name: 'userName',
          type: 'string',
          required: true,
          description: 'Email address or username associated with the account. Case-insensitive.',
        },
        {
          name: 'password',
          type: 'string',
          required: true,
          description:
            'Account password. Minimum 8 characters. Never logged or echoed in error responses.',
        },
      ],
      responseBody: [
        {
          name: 'token',
          type: 'string',
          required: false,
          nullable: false,
          description: 'Signed JWT to include in subsequent Authorization: Bearer headers.',
        },
        {
          name: 'refreshToken',
          type: 'string',
          required: false,
          nullable: false,
          description:
            'Opaque token used to obtain a new access token after expiry. Rotates on every refresh.',
        },
        {
          name: 'tokenType',
          type: 'string',
          required: false,
          nullable: false,
          description: 'Always "Bearer".',
        },
        {
          name: 'expiresAt',
          type: 'string (date-time)',
          required: false,
          nullable: false,
          description: 'Token expiration timestamp.',
        },
      ],
      responseStatus: '200 OK',
      notes: [],
    },
    {
      id: 'refresh',
      name: 'Refresh access token',
      method: 'POST',
      url: '/api/v1/auth/refreshtoken',
      description:
        'Exchanges a valid refresh token for a new access token. Refresh tokens are single-use — a new refresh token is issued with each call and the previous one is invalidated.',
      isExpanded: false,
      requestBody: [
        {
          name: 'refreshToken',
          type: 'string',
          required: true,
          description:
            'Refresh token received from a previous call to /auth/login or /auth/refreshtoken.',
        },
      ],
      responseBody: [
        {
          name: 'token',
          type: 'string',
          required: false,
          nullable: false,
          description: 'A freshly-signed JWT access token.',
        },
        {
          name: 'refreshToken',
          type: 'string',
          required: false,
          nullable: false,
          description: 'A newly rotated refresh token. Store this — the previous value is revoked.',
        },
        {
          name: 'expiresAt',
          type: 'string (date-time)',
          required: false,
          nullable: false,
          description: 'New token expiration timestamp.',
        },
      ],
      responseStatus: '200 OK',
      notes: [
        {
          title: 'Token rotation.',
          content:
            'Refresh tokens rotate on every use. Attempting to reuse a rotated token invalidates the entire token family as a defense against replay.',
          type: 'info',
        },
      ],
    },
    {
      id: 'revoke',
      name: 'Revoke session',
      method: 'POST',
      url: '/api/v1/auth/revoketoken',
      description:
        'Revokes the supplied refresh token and blacklists the paired access token for the remainder of its lifetime. Safe to call multiple times — subsequent calls with the same token are no-ops.',
      isExpanded: false,
      requestBody: [
        {
          name: 'refreshToken',
          type: 'string',
          required: true,
          description: 'The refresh token to invalidate.',
        },
      ],
      responseBody: [],
      responseStatus: '204 No Content',
    },
  ];

  codeExamples: CodeExample[] = [
    {
      lang: 'curl',
      label: 'cURL',
      code: `curl ${this.piqetLcaApiUrl}/auth/login \\
  -X POST \\
  -H "Content-Type: application/json" \\
  -d '{
    "userName":  "demo@piqet.app",
    "password":  "••••••••••"
  }'`,
    },
    {
      lang: 'node',
      label: 'Node.js',
      code: `const res = await fetch("${this.piqetLcaApiUrl}/auth/login", {
  method:  "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify({
    userName:  "demo@piqet.app",
    password:  "••••••••••",
  }),
});

const { token, refreshToken } = await res.json();

// use the token on subsequent requests
const headers = { Authorization: \`Bearer \${token}\` };`,
    },
    {
      lang: 'python',
      label: 'Python',
      code: `import requests

r = requests.post(
    "${this.piqetLcaApiUrl}/auth/login",
    json={
        "userName":  "demo@piqet.app",
        "password":  "••••••••••",
    },
    timeout=10,
)
r.raise_for_status()

tokens = r.json()
headers = {"Authorization": f"Bearer {tokens['token']}"}`,
    },
    {
      lang: 'csharp',
      label: 'C#',
      code: `using System.Net.Http.Json;

var client = new HttpClient();

var res = await client.PostAsJsonAsync(
    "${this.piqetLcaApiUrl}/auth/login",
    new {
        userName  = "demo@piqet.app",
        password  = "••••••••••"
    });

var tokens = await res.Content.ReadFromJsonAsync<LoginResponse>();
client.DefaultRequestHeaders.Authorization =
    new("Bearer", tokens.Token);`,
    },
    {
      lang: 'java',
      label: 'Java',
      code: `import java.net.URI;
import java.net.http.*;

HttpClient client = HttpClient.newHttpClient();

var body = """
  {
    "userName":  "demo@piqet.app",
    "password":  "••••••••••"
  }""";

HttpResponse<String> res = client.send(
    HttpRequest.newBuilder()
        .uri(URI.create("${this.piqetLcaApiUrl}/auth/login"))
        .header("Content-Type", "application/json")
        .POST(HttpRequest.BodyPublishers.ofString(body))
        .build(),
    HttpResponse.BodyHandlers.ofString()
);`,
    },
  ];

  responseExamples: ResponseExample[] = [
    {
      status: '200',
      statusClass: 'resp-200',
      label: '200 OK',
      code: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "refreshToken": "dGhpcyBpcyBhIHJlZnJlc2ggdG9rZW4...",
  "tokenType": "Bearer",
  "expiresAt": "2025-12-03T14:30:00Z"
}`,
    },
    {
      status: '400',
      statusClass: 'resp-401',
      label: '400',
      code: `{
  "success": false,
  "message": "",
  "error": "Invalid username or password",
  "details": "Invalid credentials",
  "validationErrors": [],
  "timestamp": "2026-05-01T09:28:11.2676802Z",
  "requestId": "76423bdd-4cde-46bb-9cef-dc22cfb67627",
  "traceId": "00-ebce0e2e8627e0179f05d614c28069b5-496b65c632d5963e-00"
}`,
    },
    {
      status: '429',
      statusClass: 'resp-403',
      label: '429',
      code: `{
  "success": false,
  "message": "Rate limit exceeded. You have made too many requests. The limit is 5 requests per 5 minutes. Please retry after 294 seconds.",
  "error": "RATE_LIMIT_EXCEEDED",
  "details": {
    "limit": 5,
    "window": 300,
    "retryAfter": 294,
    "resetTime": "2026-05-01T09:53:14.010Z"
  },
  "timestamp": "2026-05-01T09:48:20.4331327Z",
  "traceId": "00-df379b8cc3a8dddd3973b59848f1c6df-e7eb4daa0ffbb026-00"
}`,
    },
  ];

  constructor(public clipboardHelper: ClipboardHelper, private readonly router: Router) {}

  toggleEndpoint(endpoint: Endpoint): void {
    endpoint.isExpanded = !endpoint.isExpanded;
  }

  toggleNavItem(item: NavItem): void {
    item.isExpanded = !item.isExpanded;
  }

  setActiveNavChild(child: NavChild): void {
    this.navSections.forEach(section => {
      section.children?.forEach(c => (c.isActive = false));
      section.items?.forEach(item => {
        item.children.forEach(c => (c.isActive = false));
      });
    });
    child.isActive = true;
    if (child.route) {
      this.router.navigate(
        [child.route],
        child.queryParams ? { queryParams: child.queryParams } : {}
      );
    } else if (child.sectionKey) {
      this.scrollToSection(child.sectionKey);
    }
  }

  scrollToSection(sectionKey: string): void {
    const endpoint = this.endpoints.find(ep => ep.id === sectionKey);
    if (endpoint && !endpoint.isExpanded) {
      endpoint.isExpanded = true;
    }
    setTimeout(() => {
      const element = document.getElementById(sectionKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }

  navigateToRoute(route: string): void {
    this.router.navigateByUrl(route);
  }

  setCodeLang(lang: string): void {
    this.activeCodeLang = lang;
  }

  setRespStatus(status: string): void {
    this.activeRespStatus = status;
  }

  toggleTryPanel(): void {
    this.isTryPanelOpen = !this.isTryPanelOpen;
  }

  getTypeClass(type: string): string {
    const t = type.toLowerCase();
    if (t.includes('uuid')) return 'type-uuid';
    if (t.includes('datetime') || t.includes('date-time')) return 'type-datetime';
    if (t.includes('array')) return 'type-array';
    if (t.includes('object')) return 'type-object';
    if (t.includes('boolean')) return 'type-boolean';
    if (t.includes('number') || t.includes('integer')) return 'type-number';
    if (t.includes('string')) return 'type-string';
    return 'type-string';
  }

  copyUrl(text: string, event: Event): void {
    this.clipboardHelper.copyToClipboard(text, event);
  }

  isCopied(text: string): boolean {
    return this.clipboardHelper.isCopied(text);
  }

  copyCode(type: 'request' | 'response'): void {
    let code = '';
    if (type === 'request') {
      code = this.codeExamples.find(e => e.lang === this.activeCodeLang)?.code || '';
    } else {
      code = this.responseExamples.find(e => e.status === this.activeRespStatus)?.code || '';
    }
    this.clipboardHelper.copyCode(code);
    if (type === 'request') {
      this.reqCopied = true;
      setTimeout(() => (this.reqCopied = false), 1400);
    } else {
      this.respCopied = true;
      setTimeout(() => (this.respCopied = false), 1400);
    }
  }

  get filteredNavSections(): NavSection[] {
    if (!this.searchQuery.trim()) return this.navSections;
    const q = this.searchQuery.toLowerCase();
    return this.navSections
      .map(section => ({
        ...section,
        children: section.children?.filter(c => c.label.toLowerCase().includes(q)),
        items: section.items
          ?.map(item => ({
            ...item,
            isExpanded: true,
            children: item.children.filter(c => c.label.toLowerCase().includes(q)),
          }))
          .filter(item => item.children.length > 0),
      }))
      .filter(section => (section.children?.length || 0) > 0 || (section.items?.length || 0) > 0);
  }
}
